interface OfflineItem {
  id: string;
  type: 'operation' | 'team' | 'location' | 'radioChannel' | 'user';
  data: any;
  timestamp: number;
  synced: boolean;
}

class OfflineStorageManager {
  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'ASMonitorOfflineDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'offlineData';

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB');
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB opened successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('synced', 'synced', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async addOfflineItem(item: Omit<OfflineItem, 'id' | 'timestamp' | 'synced'>): Promise<string> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      const offlineItem: OfflineItem = {
        ...item,
        id: `${item.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        synced: false
      };

      const request = store.add(offlineItem);
      
      request.onsuccess = () => {
        console.log('Offline item added:', offlineItem.id);
        resolve(offlineItem.id);
      };
      
      request.onerror = () => {
        console.error('Failed to add offline item');
        reject(new Error('Failed to add offline item'));
      };
    });
  }

  async getOfflineItems(type?: string): Promise<OfflineItem[]> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      
      let request: IDBRequest;
      
      if (type) {
        const index = store.index('type');
        request = index.getAll(type);
      } else {
        request = store.getAll();
      }
      
      request.onsuccess = () => {
        const items = request.result as OfflineItem[];
        resolve(items);
      };
      
      request.onerror = () => {
        console.error('Failed to get offline items');
        reject(new Error('Failed to get offline items'));
      };
    });
  }

  async getUnsyncedItems(): Promise<OfflineItem[]> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      
      // Get all items and filter for unsynced ones
      const request = store.getAll();
      
      request.onsuccess = () => {
        const allItems = request.result as OfflineItem[];
        const unsyncedItems = allItems.filter(item => !item.synced);
        resolve(unsyncedItems);
      };
      
      request.onerror = () => {
        console.error('Failed to get unsynced items');
        reject(new Error('Failed to get unsynced items'));
      };
    });
  }

  async markAsSynced(id: string): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const item = getRequest.result as OfflineItem;
        if (item) {
          item.synced = true;
          const putRequest = store.put(item);
          
          putRequest.onsuccess = () => {
            console.log('Item marked as synced:', id);
            resolve();
          };
          
          putRequest.onerror = () => {
            console.error('Failed to mark item as synced');
            reject(new Error('Failed to mark item as synced'));
          };
        } else {
          resolve();
        }
      };
      
      getRequest.onerror = () => {
        console.error('Failed to get item for syncing');
        reject(new Error('Failed to get item for syncing'));
      };
    });
  }

  async removeItem(id: string): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      const request = store.delete(id);
      
      request.onsuccess = () => {
        console.log('Offline item removed:', id);
        resolve();
      };
      
      request.onerror = () => {
        console.error('Failed to remove offline item');
        reject(new Error('Failed to remove offline item'));
      };
    });
  }

  async clearAll(): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      const request = store.clear();
      
      request.onsuccess = () => {
        console.log('All offline data cleared');
        resolve();
      };
      
      request.onerror = () => {
        console.error('Failed to clear offline data');
        reject(new Error('Failed to clear offline data'));
      };
    });
  }

  async getStorageStats(): Promise<{ total: number; synced: number; unsynced: number }> {
    const items = await this.getOfflineItems();
    const synced = items.filter(item => item.synced).length;
    
    return {
      total: items.length,
      synced,
      unsynced: items.length - synced
    };
  }
}

// Singleton instance
export const offlineStorage = new OfflineStorageManager();

// Hook for React components
export const useOfflineStorage = () => {
  return {
    addOfflineItem: offlineStorage.addOfflineItem.bind(offlineStorage),
    getOfflineItems: offlineStorage.getOfflineItems.bind(offlineStorage),
    getUnsyncedItems: offlineStorage.getUnsyncedItems.bind(offlineStorage),
    markAsSynced: offlineStorage.markAsSynced.bind(offlineStorage),
    removeItem: offlineStorage.removeItem.bind(offlineStorage),
    clearAll: offlineStorage.clearAll.bind(offlineStorage),
    getStorageStats: offlineStorage.getStorageStats.bind(offlineStorage)
  };
}; 